import Image from 'next/image';
import { useRouter } from 'next/router';
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import useUser from '~/lib/user';
import { getPlusDiscount } from '~/lib/util/helpers';
import { Link } from '~/components/global';
import { Button } from '@appsumo/dorado-react';
interface NavItem {
  href: string;
  label: string;
}

export default function NavUser() {
  const [hoverMenu, setHoverMenu] = useState(false);
  const { user, isLoading, session } = useUser();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  const items: NavItem[] = [
    { href: '/account/', label: 'Account overview' },
    { href: '/account/profile/', label: 'Profile' },
    { href: '/account/products/', label: 'Products' },
    { href: '/plus/', label: `Get ${getPlusDiscount(session)}% off` },
    { href: '/account/credits/', label: 'Credits' },
    {
      href: 'https://go.appsumo.com/become-appsumo-affiliate?ref=account_menu',
      label: 'Affiliate program',
    },
    { href: '/account/email/', label: 'Email settings' },
    { href: `/accounts/logout/?next=${router.asPath}`, label: 'Log out' },
  ];

  const onMouseOver = useCallback(() => {
    buttonRef.current?.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Enter',
        bubbles: true,
        cancelable: true,
      }),
    );
    setHoverMenu(true);
  }, [buttonRef]);

  const onMouseLeave = useCallback(() => {
    if (hoverMenu) {
      buttonRef.current?.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          bubbles: true,
          cancelable: true,
        }),
      );
      setHoverMenu(false);
    }
  }, [buttonRef, hoverMenu]);

  return (
    <div
      className={clsx(
        'md:ml-9 md:min-w-[80px] md:items-center md:justify-end',
        { 'md:mr-[22px]': !user },
      )}
    >
      {isLoading && <div />}
      {!isLoading && !user && (
        <Button
          href="/accounts/login/"
          className="whitespace-nowrap px-4 text-center max-md:mt-10 max-md:block max-md:w-full"
        >
          Log in
        </Button>
      )}
      {!isLoading && user && (
        <div>
          <Menu as="div" className="relative z-10" onMouseEnter={onMouseOver}>
            {({ open }) => (
              <>
                <div className="flex items-center">
                  <Menu.Button ref={buttonRef} className="flex items-center">
                    <Image
                      src={`${user.avatar_small.replace(/22/g, '44')}`}
                      unoptimized
                      alt={user.email}
                      width="22"
                      height="22"
                      className="rounded-[50%]"
                    />
                    <span className="ml-4 font-header text-lg font-semibold text-black-pearl md:hidden">
                      {user.username}
                    </span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      width="16"
                      height="16"
                      className={`ml-2 transition duration-300 ease-in-out max-md:hidden ${
                        open ? 'rotate-180' : ''
                      }`}
                    />
                  </Menu.Button>
                </div>
                <Transition
                  show={open}
                  unmount={false}
                  enter="md:transition md:ease-out md:duration-100"
                  enterFrom="md:transform md:opacity-0 md:scale-95"
                  enterTo="md:transform md:opacity-100 md:scale-100"
                  leave="md:transition md:ease-in md:duration-75"
                  leaveFrom="md:transform md:opacity-100 md:scale-100"
                  leaveTo="md:transform md:opacity-0 md:scale-95"
                  className="max-md:!block"
                >
                  <Menu.Items
                    static
                    className="py-4 font-header outline-none md:absolute md:right-0 md:mt-2 md:w-48 md:rounded-md md:border md:bg-white md:shadow-lg"
                    onMouseLeave={onMouseLeave}
                  >
                    {items.map((item) => (
                      <Menu.Item key={item.href}>
                        {({ active }) => (
                          <Link
                            href={item.href}
                            className={clsx(
                              'flex w-full py-2 font-semibold text-gray-900 md:px-6',
                              {
                                'bg-gray-100/50': active,
                                'mt-4 max-md:justify-center max-md:rounded-[40px] max-md:border-2 max-md:border-deep-ocean md:mt-0':
                                  item.href.indexOf('logout') > 0,
                                'max-md:justify-between':
                                  item.href.indexOf('logout') < 0,
                              },
                            )}
                          >
                            <span
                              className={`${
                                item.href.indexOf('logout') > 0
                                  ? 'max-md:text-deep-ocean'
                                  : ''
                              }`}
                            >
                              {item.label}
                            </span>
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        </div>
      )}
    </div>
  );
}
